import axiosInstance, { axiosInstanceFast } from "@services/api/axiosInstance";
import { LogicPagePayload } from "./allData";
// import { UIPageType } from "./allData";
const API_PREFIX = "/plugin";

// Unified payload type
export type CommonApiRequestPayload = {
  requestId: number | undefined;
  plugin_id: string;
  url: string;
  method: string;
  configuration_Id: string;
  params: any;
  headers: { key: string; value: string }[];
  ssl_verify: boolean;
  auth?:
    | {
        type: "none" | "bearer";
        token: string;
      }
    | {
        type: "basic";
        username: string;
        password: string;
      }
    | {
        username: string;
        password: string;
      }
    | undefined;
  body: any;
  isLoop: boolean;
  isSave: boolean;
  loopVariable: string | null;
  loopSourcePath: string | null;
  checkpoint: {
    produces: {
      enabled: boolean;
      sourcePath: string | null;
    };
    consumes: {
      enabled: boolean;
      paramTemplate: string | null;
    };
  };
  chainedFrom: number | null;
  flag: number;
  config?: any;
  data?: any;
  loopSourceRequest?: number | null;
  loopSourceRequestBundle?: string | null;
  body_type?: string;
  raw_type?: string | null;
  pagination?: any;
  filteration?: {
    type?: string;
    parameter?: any;
    default_cron_start_time?: string | null;
    start_key?: string | null;
    end_key?: string | null;
    custom_date_format?: string | null;
  };
};

// Helper function to ensure all required fields have proper values
const normalizePayload = (
  payload: Partial<CommonApiRequestPayload>,
): Partial<CommonApiRequestPayload> => {
  return {
    requestId: payload.requestId ?? undefined,
    plugin_id: payload.plugin_id ?? "",
    url: payload.url ?? "",
    method: payload.method ?? "GET",
    configuration_Id: payload.configuration_Id ?? "",
    params: payload.params ?? { type: "none", parameter: [] },
    headers: payload.headers ?? [],
    ssl_verify: payload.ssl_verify ?? true,
    auth: payload.auth ?? { type: "none", token: "" },
    body: payload.body ?? null,
    body_type: payload.body_type ?? "none",
    raw_type: payload.raw_type ?? null,
    isLoop: payload.isLoop ?? false,
    isSave: payload.isSave ?? false,
    loopVariable: payload.loopVariable ?? null,
    loopSourcePath: payload.loopSourcePath ?? null,
    loopSourceRequest: payload.loopSourceRequest ?? null,
    loopSourceRequestBundle: payload.loopSourceRequestBundle ?? null,
    checkpoint: payload.checkpoint ?? {
      produces: { enabled: false, sourcePath: null },
      consumes: { enabled: false, paramTemplate: null },
    },
    chainedFrom: payload.chainedFrom ?? null,
    flag: payload.flag ?? 0,
    config: payload.config ?? {},
    data: payload.data ?? {},
    pagination: payload.pagination ?? { type: "none" },
    filteration: payload.filteration
      ? {
          ...payload.filteration,
          type:
            payload.filteration.type === "custom"
              ? "custom_date_format"
              : payload.filteration.type || "none",
        }
      : {
          type: "none",
          parameter: null,
          default_cron_start_time: "none",
          start_key: "",
          end_key: "",
          custom_date_format: "",
        },
    ...payload,
  };
};

// Unified request function
export const sendRequest = async (
  payload: Partial<CommonApiRequestPayload>,
  signal?: AbortSignal,
) => {
  const startTime = performance.now();

  // Normalize payload to ensure all required fields are present
  const normalizedPayload = normalizePayload(payload);

  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/common-api`,
    normalizedPayload,
    {
      headers: {
        "Content-Type": "application/json",
      },
      signal,
    },
  );

  const endTime = performance.now();
  const durationMs = endTime - startTime;
  const durationSec = durationMs / 1000;

  const data = response.data;
  const responseText = typeof data === "string" ? data : JSON.stringify(data);
  const sizeBytes = new TextEncoder().encode(responseText).length;
  const sizeKB = (sizeBytes / 1024).toFixed(2);
  const speedKBps = (sizeBytes / 1024 / durationSec).toFixed(2);

  // Extract Content-Type and infer type
  const contentType = response.headers["content-type"] || "unknown";

  let responseType = "Unknown";
  if (contentType.includes("application/json")) {
    responseType = "JSON";
  } else if (contentType.includes("text/csv")) {
    responseType = "CSV";
  } else if (contentType.includes("text/html")) {
    responseType = "HTML";
  } else if (contentType.includes("text/plain")) {
    responseType = "Text";
  } else if (
    contentType.includes("application/xml") ||
    contentType.includes("text/xml")
  ) {
    responseType = "XML";
  }

  return {
    status: response.status,
    headers: response.headers,
    body: data,
    metrics: {
      status: response.status,
      durationMs: durationMs.toFixed(2),
      sizeKB,
      speedKBps,
      responseType,
      contentType,
    },
  };
};

// fetchPluginInfo function to retrieve plugin information
export const fetchPluginInfo = async (pluginId: string) => {
  const response = await axiosInstanceFast.get(
    `${API_PREFIX}/get-info/${pluginId}/`,
  );
  return response.data;
};

// get all the ui page data for a plugin
export const fetchUIPageData = async (pluginId: string) => {
  const response = await axiosInstanceFast.get(
    `${API_PREFIX}/get-ui-page/${pluginId}/`,
  );
  return response.data;
};

// get all logic page data
export const fetchLogicPageData = async (pluginId: string) => {
  const response = await axiosInstanceFast.get(
    `${API_PREFIX}/get-logic-page/${pluginId}/`,
  );
  return response.data;
};

// saveFullPluginInfo function to save or update plugin information
export const saveFullPluginInfo = async (pluginData: {
  pluginName: string;
  version: string;
  tags: string[];
  description1: string;
  description2: string;
  ls_version: { minimum: string };
  id: string;
  interpreter: { type: string; version: string };
  type: string;
  author: string;
  lsUser: string;
  createdDate: string;
  updatedDate: string;
  productType: string;
  ls_product_type: any[];
  pluginDependency: { name: string; id: string }[];
  pluginLogo: string;
  is_edit: boolean;
}) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/create-info`,
    pluginData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  return response.data;
};

// create-ui-page function to save UI page information
export const saveUIPageInfo = async (uiPageData: {
  plugin_id: string;
  pages: any[];
  is_edit: boolean;
}) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/create-ui-page/${uiPageData.plugin_id}`,
    uiPageData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  return response.data;
};

// get the logics
export const fetchLogicPageInfo = async (pluginId: string) => {
  const response = await axiosInstanceFast.get(
    `${API_PREFIX}/get-logic-page/${pluginId}/`,
  );
  return response.data;
};

// create / update the logics
export const saveLogicPageInfo = async (
  plugin_id: string,
  logicPageData: LogicPagePayload,
) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/create-logic-page/${plugin_id}`,
    logicPageData,
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  return response.data;
};

// Fetch all log mapping fields
export const fetchLogMappingFields = async (signal?: AbortSignal) => {
  const response = await axiosInstance.post(
    "/get-all-log-mappings/",
    {},
    {
      signal,
    },
  );
  return response.data;
};

// create-config-page
export const createConfigPage = async ({
  plugin_id,
  tab_id,
  data,
  type_page,
  uid,
}: {
  plugin_id: string;
  tab_id: string;
  data: any[];
  type_page?: any;
  uid?: string | number;
}) => {
  const body: any = { tab_id, data, type_page };
  if (uid) body.uid = uid;

  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/create-config-page/${plugin_id}`,
    body,
    { headers: { "Content-Type": "application/json" } },
  );

  return response.data;
};

// get-config-page
export const fetchConfigPage = async (plugin_id: string, type_page: string) => {
  const response = await axiosInstanceFast.get(
    `/plugin/get-config-page/${plugin_id}`,
    {
      params: { type_page },
    },
  );
  return response.data;
};

// delete-config-page
export const deleteConfigPage = async ({
  plugin_id,
  tab_id,
  uid,
  type_page,
}: {
  plugin_id: string;
  tab_id: string;
  uid: string;
  type_page?: string;
}) => {
  const response = await axiosInstanceFast.delete(
    `/plugin/delete-config-page/${plugin_id}`,
    {
      data: {
        tab_id,
        uid,
        type_page,
      },
    },
  );
  return response.data;
};

// Download plugin help document
export const downloadPluginHelp = async (plugin_id: string, uid: string) => {
  const response = await axiosInstanceFast.post(
    `/plugin/download-plugin-help`,
    { plugin_id, uid, plugin_type: "meshconnectx" },
    { responseType: "blob" },
  );
  return response;
};

// common delete api
export const commonDeleteApi = async (
  plugin_id: string,
  delete_type: string,
  bundle_uid: string,
  request_uid: string,
  variable_uid: string,
) => {
  const response = await axiosInstanceFast.post(
    `/plugin/meshconnectx-common-delete`,
    {
      plugin_id,
      delete_type,
      bundle_uid,
      request_uid,
      variable_uid,
    },
  );
  return response.data;
};

// export plugin
export const exportPlugin = async (plugin_id: string) => {
  try {
    const response = await axiosInstanceFast.post(
      `${API_PREFIX}/meshconnectx-export-plugin/${plugin_id}`,
      {},
      {
        responseType: "blob",
      },
    );
    return response;
  } catch (error) {
    console.error("Export plugin failed:", error);
    throw error;
  }
};

// delete save logs
export const deleteSaveLogMapping = async (
  log_uid: string,
  plugin_id: string,
) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/meshconnectx-delete-save-log/${plugin_id}`,
    {
      log_uid,
      plugin_id,
    },
  );

  return response.data;
};

// delete dList mapping
export const deleteDatalistMapping = async (
  datalist_title: string,
  plugin_id: string,
) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/meshconnectx-delete-datalist/${plugin_id}`,
    {
      datalist_title,
      plugin_id,
    },
  );
  return response.data;
};

// check dList name
export const checkDatalistName = async (
  datalist_title: string,
  plugin_id: string,
) => {
  const response = await axiosInstanceFast.post(
    `${API_PREFIX}/meshconnectx-check-datalist/${plugin_id}`,
    {
      datalist_title,
      plugin_id,
    },
  );

  return response.data;
};

// acivate / deactivate bundle
export const toggleBundleStatus = async (
  plugin_id: string,
  bundle_id: string,
  status: "active" | "deactive",
) => {
  const res = await axiosInstanceFast.post(
    `${API_PREFIX}/meshconnectx-bundle-status/${plugin_id}`,
    {
      plugin_id,
      bundle_id,
      status,
    },
  );
  return res.data;
};
